import { BaseTool } from "../BaseTool.js";
import { LineObject } from "../../data/models/LineObject.js";
import { createDotData, createStyledDot, findNearestDot, getToolOptions, isPrimaryPointer } from "../toolHelpers.js";

export class LineTool extends BaseTool {
  constructor() {
    super("line");
    this.draftStart = null;
  }

  onPointerDown(context) {
    if (!isPrimaryPointer(context)) {
      return;
    }

    this.draftStart = this.resolveDraftPoint(context, "preview-line-start");
    context.setPreview(this.createPreview(context, this.draftStart, this.draftStart));
  }

  onPointerMove(context) {
    if (!this.draftStart) {
      return;
    }

    const draftEnd = this.resolveDraftPoint(context, "preview-line-end");
    context.setPreview(this.createPreview(context, this.draftStart, draftEnd));
  }

  onPointerUp(context) {
    if (!this.draftStart || !isPrimaryPointer(context)) {
      return;
    }

    const draftEnd = this.resolveDraftPoint(context, "preview-line-end");

    if (isSameDraftPoint(this.draftStart, draftEnd)) {
      this.cancel();
      context.clearPreview();
      return;
    }

    const startDot = this.draftStart.dot ?? createStyledDot(context, this.draftStart.point);
    const endDot = draftEnd.dot ?? createStyledDot(context, draftEnd.point);
    const toolOptions = getToolOptions(context.config);

    context.dataManager.addObject(
      new LineObject({
        id: context.dataManager.createUniqueObjectId("line"),
        points: [startDot.id, endDot.id],
        strokeColor: toolOptions.strokeColor,
        fillColor: "transparent",
        lineWidth: toolOptions.lineWidth
      })
    );

    this.cancel();
    context.clearPreview();
    context.render();
  }

  cancel() {
    this.draftStart = null;
  }

  resolveDraftPoint(context, previewId) {
    const dot = findNearestDot({
      documentModel: context.getDocument(),
      screenPoint: context.screenPoint,
      worldToScreen: context.worldToScreen,
      tolerance: context.config.tools.pointHitTolerance
    });

    if (dot) {
      return { dot, point: { x: dot.x, y: dot.y } };
    }

    return { dot: null, point: { ...context.worldPoint }, previewId };
  }

  createPreview(context, draftStart, draftEnd) {
    const toolOptions = getToolOptions(context.config);
    const startDot = draftStart.dot ?? createDotData(context, draftStart.point, draftStart.previewId);
    const endDot = draftEnd.dot ?? createDotData(context, draftEnd.point, draftEnd.previewId);
    const dots = startDot.id === endDot.id ? [startDot] : [startDot, endDot];
    const objects = startDot.id === endDot.id
      ? []
      : [{
        id: "preview-line",
        type: "line",
        points: [startDot.id, endDot.id],
        strokeColor: toolOptions.strokeColor,
        fillColor: "transparent",
        lineWidth: toolOptions.lineWidth
      }];

    return {
      type: "line",
      dots,
      objects,
      opacity: 0.72
    };
  }
}

function isSameDraftPoint(start, end) {
  if (start.dot && end.dot) {
    return start.dot.id === end.dot.id;
  }

  return start.point.x === end.point.x && start.point.y === end.point.y;
}
